import React, { useState } from 'react';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { Icon } from '@iconify/react';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_ABBREV = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Convert minutes to time string (e.g., 540 -> "09:00")
const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

// Get type color
const getTypeColor = (type) => {
  switch (type) {
    case 'always': return 'success';
    case 'never': return 'error';
    case 'weekly': return 'primary';
    default: return 'default';
  }
};

export default function AccessLevelPreview({ contract, onCopy }) {
  const [showJson, setShowJson] = useState(false);

  // Count active days
  const activeDays = contract.weeklySchedule.days.filter(d => d.enabled).length;

  // Get summary of periods
  const getPeriodsSummary = () => {
    const summaries = [];
    contract.weeklySchedule.days.forEach((day, index) => {
      if (day.enabled && day.periods.length > 0) {
        const periodsStr = day.periods
          .map(p => `${minutesToTime(p.startMinutes)}-${minutesToTime(p.endMinutes)}`)
          .join(', ');
        summaries.push({ day: DAY_ABBREV[index], periods: periodsStr });
      }
    });
    return summaries;
  };

  const periodsSummary = getPeriodsSummary();

  return (
    <Card>
      <CardHeader 
        title="Contract Preview"
        subheader="Unified access schedule model"
        action={
          <Box>
            <Tooltip title="Copy JSON to clipboard">
              <IconButton onClick={onCopy} size="small">
                <Icon icon="mdi:content-copy" width={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title={showJson ? "Hide JSON" : "Show JSON"}>
              <IconButton onClick={() => setShowJson(!showJson)} size="small">
                <Icon icon={showJson ? "mdi:code-tags" : "mdi:code-json"} width={20} />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <Box sx={{ px: 3, pb: 3 }}>
        {/* Summary View */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Name:
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {contract.name || 'Unnamed'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Type:
            </Typography>
            <Chip 
              label={contract.type.toUpperCase()} 
              size="small" 
              color={getTypeColor(contract.type)}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Active Days:
            </Typography>
            <Typography variant="body2">
              {activeDays} / 7
            </Typography>
          </Box>

          {/* Schedule Summary */}
          {periodsSummary.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Schedule:
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 0.5,
                bgcolor: 'grey.50',
                borderRadius: 1,
                p: 1.5
              }}>
                {periodsSummary.map(({ day, periods }) => (
                  <Box key={day} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'medium', 
                        minWidth: 40,
                        color: 'primary.main'
                      }}
                    >
                      {day}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {periods}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {activeDays === 0 && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: 'error.lighter',
              borderRadius: 1,
              textAlign: 'center'
            }}>
              <Icon icon="mdi:alert-circle" width={24} style={{ color: '#f44336' }} />
              <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                No access periods defined
              </Typography>
            </Box>
          )}
        </Box>

        {/* JSON View */}
        <Collapse in={showJson}>
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: '#1e1e1e',
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: 400
          }}>
            <Typography 
              component="pre" 
              variant="body2" 
              sx={{ 
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                color: '#d4d4d4',
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {JSON.stringify(contract, null, 2)}
            </Typography>
          </Box>
        </Collapse>
      </Box>
    </Card>
  );
}

